import React, { useState, useMemo, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Tag,
  Tabs,
  Button,
  Input,
  Descriptions,
  Badge,
  message,
} from "antd";
import dayjs from "dayjs";
import { CreditCardOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import UserProfileLayout, {
  AvatarCard,
  InfoSection,
  StatusBadge,
  type InfoItem,
} from "../../../Common/UserProfileLayout";
import {
  LandingMeetingTime,
  UserMembership,
  useSendRegistrationLink,
  useUserGetById,
  useUserUpdate,
  useRenewSubscription,
} from "../../../../hooks/api/users";
import { useVaadSendForm } from "../../../../hooks/api/vaads";
import { getUserStatusColor } from "../index";
import { SubscriptionStatusDisplay } from "../../Vaadim/SinglePage/VaadTabs/MembersTab";
import { useAuth, UserRoleType } from "../../../../store/auth";
import { getUserRequestPath } from "../../../../utils";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const capitalize = (str: string | null | undefined): string => {
  if (!str) return "-";
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getAnswerColor = (answer: string | null | undefined): string => {
  const normalized = (answer ?? "").trim().toLowerCase();
  if (normalized === "works well" || normalized === "yes") return "#52C41A";
  if (normalized === "not possible" || normalized === "no") return "#F5222D";
  return "#1677FF";
};

const MeetingStatusTag: React.FC<{ answer: string | null | undefined }> = ({
  answer,
}) => (
  <Tag color={getAnswerColor(answer)} variant="outlined">
    {answer || "-"}
  </Tag>
);

const MeetingAvailability: React.FC<{ meetingTimes: LandingMeetingTime[] }> = ({
  meetingTimes,
}) => {
  if (!meetingTimes.length) {
    return (
      <Text type="secondary" style={{ paddingLeft: 16 }}>
        No availability provided
      </Text>
    );
  }

  const items: InfoItem[] = meetingTimes.map((slot) => ({
    label: slot.time,
    value: <MeetingStatusTag answer={slot.answer} />,
  }));

  return <InfoSection title="" items={items} />;
};

const VaadAbout: React.FC<{ membership: UserMembership }> = ({
  membership,
}) => {
  const { id } = useParams();
  const sendRegistrationLink = useSendRegistrationLink();
  const renewSubscription = useRenewSubscription();
  const sendForm = useVaadSendForm();

  const vaadStatusText = capitalize(membership.vaadStatus);
  const subscriptionStatusText = capitalize(membership.subscriptionStatus);
  const subscriptionTypeText = capitalize(membership.subscriptionType);
  const userStatusText = capitalize(membership.userStatus);

  const isNotStarted = membership.vaadStatus === "not_started";
  const isCancelled =
    membership.subscriptionStatus === "canceled" ||
    membership.subscriptionStatus === "cancelled";

  const handleSendForm = async () => {
    try {
      await sendForm.fetch(undefined, `${membership.vaad.id}/send-form/${id}`);
      message.success("Form sent successfully");
    } catch (error) {
      message.error("Failed to send form");
    }
  };

  const handleSendRegistrationLink = async () => {
    try {
      await sendRegistrationLink.fetch(
        undefined,
        `${id}/memberships/${membership.id}/send-registration-link`,
      );
      message.success("Registration link sent successfully");
    } catch (error) {
      message.error("Failed to send registration link");
    }
  };

  const handleRenew = async () => {
    try {
      await renewSubscription.fetch(
        undefined,
        `${id}/memberships/${membership.id}/send-renew-link`,
      );
      message.success("Renewal email sent successfully");
    } catch (error) {
      message.error("Failed to send renewal email");
    }
  };

  const items: InfoItem[] = [
    {
      label: "Vaad Status",
      value: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <StatusBadge
            type="tag"
            status={
              membership.vaadStatus === "active" ||
                membership.vaadStatus === "started"
                ? "success"
                : "error"
            }
            text={vaadStatusText}
          />
          <div style={{ display: "flex", gap: 8 }}>
            {isNotStarted ? (
              <Button type="primary" ghost onClick={handleSendForm}>
                Send form
              </Button>
            ) : (
              <Button type="primary" ghost onClick={handleSendRegistrationLink}>
                Send registration link
              </Button>
            )}
          </div>
        </div>
      ),
    },
  ];

  if (membership.subscriptionType) {
    items.push({
      label: "Subscription Type",
      value: (
        <StatusBadge
          type="tag"
          status={
            membership.subscriptionType === "yearly" ? "success" : "warning"
          }
          text={subscriptionTypeText}
        />
      ),
    });
  }

  if (membership.subscriptionStatus) {
    items.push({
      label: "Subscription Status",
      value: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Badge
            color={
              SubscriptionStatusDisplay[
                membership.subscriptionStatus as keyof typeof SubscriptionStatusDisplay
              ]?.color || "rgba(0,0,0,.15)"
            }
            text={
              SubscriptionStatusDisplay[
                membership.subscriptionStatus as keyof typeof SubscriptionStatusDisplay
              ]?.text ?? subscriptionStatusText
            }
          />
          {isCancelled && (
            <Button type="primary" ghost onClick={handleRenew}>
              Renew
            </Button>
          )}
        </div>
      ),
    });
  }

  items.push(
    {
      label: "User Status",
      value: (
        <Badge
          color={getUserStatusColor(membership.userStatus)}
          text={userStatusText}
        />
      ),
    },
    {
      label: "Discount",
      value: `${membership.discountValue || 0}%`,
    },
  );

  if (membership.discountEndDate) {
    items.push({
      label: "Discount End Date",
      value: dayjs(membership.discountEndDate).format("MMM DD, YYYY"),
    });
  }

  if (membership.numberOfMonths != null) {
    items.push({
      label: "Discount duration",
      value: `${membership.numberOfMonths}`,
    });
  }

  return (
    <div>
      <InfoSection title="" items={items} />
      {membership.stripeSubscriptionId && (
        <Row
          align="middle"
          style={{
            marginTop: 16,
            padding: "12px 16px",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        >
          <Col span={16}>
            <Text>This student is up to date on their tuition payments.</Text>
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<CreditCardOutlined />}
              onClick={() => {
                if (membership?.stripeSubscriptionId) {
                  window.open(
                    `https://dashboard.stripe.com/subscriptions/${membership.stripeSubscriptionId}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }
              }}
            >
              Manage in Stripe
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
};

const UserViewContent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from || "/users";
  const [note, setNote] = useState("");
  const userGetById = useUserGetById(id);
  const userUpdate = useUserUpdate(id);
  const userData = userGetById.data;
  const { user } = useAuth();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  useEffect(() => {
    setNote(userData?.note ?? "");
  }, [userData?.note]);

  const memberships = useMemo(() => {
    if (!userData?.memberships) return [];
    return userData.memberships;
  }, [userData?.memberships]);

  const landingFormData = userData?.landingFormData;

  const handleSave = async () => {
    try {
      await userUpdate.fetch({ note }, id);
      message.success("Note saved successfully");
    } catch (error) {
      message.error("Failed to save note");
    }
  };

  const extra = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      <Button onClick={() => navigate(backTo)}>Cancel</Button>
      <Button type="primary" loading={userUpdate.loading} onClick={handleSave}>
        Save
      </Button>
    </div>
  );

  const contactItems: InfoItem[] = [
    { label: "Full Name", value: userData?.fullName || "-" },
    { label: "Phone", value: userData?.phone || "-" },
    { label: "Email", value: userData?.email || "-" },
    { label: "Address", value: userData?.address || "-" },
    {
      label: "Join Date",
      value: userData?.createdAt
        ? dayjs(userData.createdAt).format("MMM DD, YYYY")
        : "-",
    },
    {
      label: "Last Login",
      value: userData?.lastLoginAt
        ? dayjs.utc(userData.lastLoginAt).format("MMM DD, YYYY, HH:mm")
        : "-",
    },
  ];

  return (
    <SinglePageContainer
      title="User Info"
      onBack={() => navigate(backTo)}
      extra={extra}
      cardLoading={userGetById.loading}
    >
      <UserProfileLayout
        avatarCard={
          <AvatarCard
            name={userData?.fullName || "-"}
            avatar={userData?.photoUrl}
          />
        }
      >
        <InfoSection
          title="Contact Information"
          items={contactItems}
          {...(isAdmin ? { onEdit: () => navigate(`/users/${id}/edit`) } : {})}
        />

        {memberships.map((membership) => {
          const hasAvailability =
            membership.vaadStatus !== "active" &&
            !!landingFormData &&
            landingFormData.vaadId === membership.vaad.id &&
            !!landingFormData.meetingTimes?.length;

          return (
            <div key={membership.id} style={{ marginBottom: 32 }}>
              <Title level={5} style={{ marginBottom: 16, paddingLeft: 16 }}>
                {membership.vaadId}: {membership.vaadName}
              </Title>

              {hasAvailability ? (
                <Tabs
                  defaultActiveKey="availability"
                  tabBarStyle={{ marginLeft: 24, marginBottom: 12 }}
                >
                  <TabPane tab="Meeting Availability" key="availability">
                    <MeetingAvailability
                      meetingTimes={landingFormData.meetingTimes ?? []}
                    />
                  </TabPane>
                  <TabPane tab="About" key="about">
                    <VaadAbout membership={membership} />
                  </TabPane>
                </Tabs>
              ) : (
                <>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 12,
                      paddingLeft: 16,
                    }}
                  >
                    About
                  </Text>
                  <VaadAbout membership={membership} />
                </>
              )}
            </div>
          );
        })}

        <div>
          <Title level={5} style={{ marginBottom: 12, paddingLeft: 16 }}>
            Note
          </Title>
          <TextArea
            rows={3}
            placeholder="Type here"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ borderRadius: 8 }}
          />
        </div>
      </UserProfileLayout>
    </SinglePageContainer>
  );
};

export default UserViewContent;
